import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowUpRight, Circle, Pen, ArrowCounterClockwise, X, Check } from '@phosphor-icons/react';

/**
 * PhotoAnnotator — lets the homeowner draw arrows / circles / freehand
 * over a defect photo to point out exactly where the issue is. Used in
 * the pin modal (Floorplan.jsx) and snag editor (ProjectDetail.jsx).
 *
 * Design notes:
 *  - Strokes are stored as plain JSON (not flattened pixels) so the
 *    homeowner can edit them later without quality loss, and the PDF
 *    renderer can re-draw them in vector for crisp output at any size.
 *  - Coordinates are normalised to 0..1 of the photo bounds — that way
 *    a phone-resolution canvas and an A4 PDF render the same overlay
 *    proportionally.
 *  - Active gesture lives in a ref to dodge stale closures during fast
 *    mouse/touch sequences (each pointer-move would otherwise see the
 *    React state from a previous render and lose intermediate points).
 *  - Canvas is resized whenever the underlying image's bounding rect
 *    changes — we observe both `onLoad` AND window resize so a slow-
 *    loading image can't end up with a 0×0 canvas underneath.
 *  - mouseleave does NOT auto-commit the stroke: small cursor jitter
 *    near the canvas edge used to prematurely end arrow/circle drags.
 */

const COLOURS = ['#1FA5CF', '#E53935', '#F59E0B', '#FFFFFF', '#09090B'];
const TOOLS = [
    { id: 'arrow', icon: ArrowUpRight, label: 'Arrow' },
    { id: 'circle', icon: Circle, label: 'Circle' },
    { id: 'pen', icon: Pen, label: 'Freehand' },
];

export default function PhotoAnnotator({ photoSrc, initialAnnotations = [], onSave, onCancel }) {
    const wrapperRef = useRef(null);
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const [tool, setTool] = useState('arrow');
    const [colour, setColour] = useState('#1FA5CF');
    const [strokes, setStrokes] = useState(() => initialAnnotations || []);
    const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

    // Active gesture lives in refs (not state) so every pointer-move
    // sees the up-to-date stroke without depending on React re-renders.
    // A small `tick` state forces a redraw any time the ref changes.
    const drawingRef = useRef(null);
    const [tick, setTick] = useState(0);
    const bumpTick = useCallback(() => setTick(t => (t + 1) % 1024), []);

    /** Resize the canvas to match the underlying image's current
     *  bounding rect. Called from multiple places (image onLoad,
     *  window resize, after imgSize state changes) so a slow-loading
     *  image can't trap us with a 0×0 canvas. */
    const syncCanvasToImage = useCallback(() => {
        const img = imgRef.current;
        const c = canvasRef.current;
        if (!img || !c) return;
        const rect = img.getBoundingClientRect();
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);
        if (w === 0 || h === 0) return;  // image not laid out yet — try again on next event
        const dpr = window.devicePixelRatio || 1;
        c.width = w * dpr;
        c.height = h * dpr;
        c.style.width = `${w}px`;
        c.style.height = `${h}px`;
        const ctx = c.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        setImgSize(prev => (prev.w === w && prev.h === h ? prev : { w, h }));
    }, []);

    // Initial fit + window resize. We DON'T depend on photoSrc here —
    // the onLoad handler on <img> calls syncCanvasToImage too, so a
    // fresh photo correctly triggers a re-fit when it actually arrives.
    useEffect(() => {
        syncCanvasToImage();
        window.addEventListener('resize', syncCanvasToImage);
        return () => window.removeEventListener('resize', syncCanvasToImage);
    }, [syncCanvasToImage]);

    // Re-draw whenever strokes / live gesture / size change. The tick
    // counter is bumped from start/move/end so the active stroke (which
    // lives in a ref) is reflected on screen during the gesture.
    useEffect(() => {
        redraw();
    }, [strokes, imgSize, tick]);

    const ptFromEvent = (e) => {
        const c = canvasRef.current;
        if (!c) return null;
        const r = c.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) return null;
        const native = e.nativeEvent || e;
        // touchend uses changedTouches; touchstart/touchmove use touches.
        const touchList = native.touches && native.touches.length ? native.touches
                        : (native.changedTouches && native.changedTouches.length ? native.changedTouches : null);
        const cx = touchList ? touchList[0].clientX : native.clientX;
        const cy = touchList ? touchList[0].clientY : native.clientY;
        return {
            x: Math.max(0, Math.min(1, (cx - r.left) / r.width)),
            y: Math.max(0, Math.min(1, (cy - r.top) / r.height)),
        };
    };

    const start = (e) => {
        e.preventDefault();
        const p = ptFromEvent(e);
        if (!p) return;
        // Seed BOTH endpoints with the same point so the very first redraw
        // (before any move event) has a valid `b` for arrow/circle — and
        // the user gets a tiny visible marker the moment they tap.
        drawingRef.current = { tool, colour, points: [p, p] };
        bumpTick();
    };

    const move = (e) => {
        const d = drawingRef.current;
        if (!d) return;
        e.preventDefault();
        const p = ptFromEvent(e);
        if (!p) return;
        // Arrow / circle: replace the live endpoint. Pen: accumulate
        // (we drop the duplicate seed point on the first move).
        if (d.tool === 'pen') {
            if (d.points.length === 2 && d.points[0].x === d.points[1].x && d.points[0].y === d.points[1].y) {
                d.points = [d.points[0], p];
            } else {
                d.points.push(p);
            }
        } else {
            d.points = [d.points[0], p];
        }
        bumpTick();
    };

    const end = (e) => {
        const d = drawingRef.current;
        if (!d) return;
        if (e?.preventDefault) e.preventDefault();
        drawingRef.current = null;
        // Drop trivial single-point pen strokes to avoid clutter.
        if (d.points.length < 2 && d.tool === 'pen') {
            bumpTick();
            return;
        }
        // For arrow/circle, a tap without movement leaves both seeded
        // points identical → almost invisible. Synthesise a small offset
        // so a single tap still produces a usable mark.
        if (d.tool !== 'pen' && (d.points.length < 2 || (d.points[0].x === d.points[1].x && d.points[0].y === d.points[1].y))) {
            const [p0] = d.points;
            const synthesised = { ...d, points: [p0, { x: Math.min(1, p0.x + 0.04), y: Math.min(1, p0.y + 0.04) }] };
            setStrokes(s => [...s, synthesised]);
        } else {
            setStrokes(s => [...s, d]);
        }
        bumpTick();
    };

    const undo = () => setStrokes(s => s.slice(0, -1));
    const clear = () => setStrokes([]);

    const drawStroke = (ctx, s, w, h) => {
        if (!s || !s.points || s.points.length === 0) return;
        // Arrow/circle need a valid second endpoint; pen tolerates a
        // single point (renders a dot via lineCap='round').
        if (s.tool !== 'pen' && s.points.length < 2) return;
        ctx.strokeStyle = s.colour;
        ctx.fillStyle = s.colour;
        ctx.lineWidth = 4;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        if (s.tool === 'pen') {
            ctx.beginPath();
            s.points.forEach((p, i) => {
                if (i === 0) ctx.moveTo(p.x * w, p.y * h);
                else ctx.lineTo(p.x * w, p.y * h);
            });
            ctx.stroke();
        } else if (s.tool === 'circle') {
            const [a, b] = s.points;
            const cx = ((a.x + b.x) / 2) * w;
            const cy = ((a.y + b.y) / 2) * h;
            const rx = Math.abs(b.x - a.x) * w / 2;
            const ry = Math.abs(b.y - a.y) * h / 2;
            ctx.beginPath();
            ctx.ellipse(cx, cy, Math.max(rx, 6), Math.max(ry, 6), 0, 0, Math.PI * 2);
            ctx.stroke();
        } else if (s.tool === 'arrow') {
            const [a, b] = s.points;
            const ax = a.x * w, ay = a.y * h, bx = b.x * w, by = b.y * h;
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(bx, by);
            ctx.stroke();
            const angle = Math.atan2(by - ay, bx - ax);
            const head = 14;
            ctx.beginPath();
            ctx.moveTo(bx, by);
            ctx.lineTo(bx - head * Math.cos(angle - Math.PI / 6), by - head * Math.sin(angle - Math.PI / 6));
            ctx.lineTo(bx - head * Math.cos(angle + Math.PI / 6), by - head * Math.sin(angle + Math.PI / 6));
            ctx.closePath();
            ctx.fill();
        }
    };

    const redraw = () => {
        const c = canvasRef.current;
        if (!c) return;
        const ctx = c.getContext('2d');
        // Clear in CSS pixels (we already set the dpr transform in syncCanvasToImage).
        ctx.clearRect(0, 0, c.width, c.height);
        const { w, h } = imgSize;
        if (w === 0 || h === 0) return;
        strokes.forEach(s => drawStroke(ctx, s, w, h));
        if (drawingRef.current) drawStroke(ctx, drawingRef.current, w, h);
    };

    // The active stroke isn't a useEffect dep (it lives in a ref) so we
    // also redraw here on every render triggered by bumpTick.
    return (
        <div className="fixed inset-0 z-50 bg-black/85 flex flex-col" data-testid="photo-annotator">
            <div className="flex items-center justify-between px-4 py-3 bg-[#09090B] text-white">
                <div className="flex items-center gap-2">
                    <Pen size={18} weight="bold" />
                    <span className="font-display font-semibold">Mark up photo</span>
                </div>
                <button type="button" onClick={onCancel} aria-label="Close" data-testid="annotator-close" className="p-1 hover:bg-white/10"><X size={20} /></button>
            </div>
            <div ref={wrapperRef} className="flex-1 flex items-center justify-center p-3 overflow-hidden relative">
                <div className="relative max-h-full max-w-full inline-block">
                    <img
                        ref={imgRef}
                        src={photoSrc}
                        alt="Defect"
                        crossOrigin="anonymous"
                        onLoad={syncCanvasToImage}
                        className="block max-h-[70vh] max-w-full select-none pointer-events-none"
                    />
                    <canvas
                        ref={canvasRef}
                        data-testid="annotator-canvas"
                        onMouseDown={start}
                        onMouseMove={move}
                        onMouseUp={end}
                        onTouchStart={start}
                        onTouchMove={move}
                        onTouchEnd={end}
                        onTouchCancel={() => { drawingRef.current = null; bumpTick(); }}
                        className="absolute inset-0 cursor-crosshair touch-none"
                    />
                </div>
            </div>
            <div className="bg-[#09090B] text-white px-3 py-3 flex flex-wrap items-center gap-2 justify-between">
                <div className="flex items-center gap-1">
                    {TOOLS.map(t => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTool(t.id)}
                            data-testid={`annotator-tool-${t.id}`}
                            className={`p-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider ${tool === t.id ? 'bg-[#1FA5CF] text-white' : 'bg-white/10 hover:bg-white/20'}`}
                        >
                            <t.icon size={14} weight="bold" /> {t.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-1.5">
                    {COLOURS.map(c => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setColour(c)}
                            data-testid={`annotator-colour-${c.replace('#', '')}`}
                            aria-label={`Colour ${c}`}
                            className={`w-7 h-7 border ${colour === c ? 'border-white ring-2 ring-[#1FA5CF]' : 'border-white/30'}`}
                            style={{ background: c }}
                        />
                    ))}
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={undo}
                        disabled={!strokes.length}
                        data-testid="annotator-undo"
                        className="p-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider bg-white/10 hover:bg-white/20 disabled:opacity-40"
                    >
                        <ArrowCounterClockwise size={14} weight="bold" /> Undo
                    </button>
                    <button
                        type="button"
                        onClick={clear}
                        disabled={!strokes.length}
                        data-testid="annotator-clear"
                        className="p-2 font-mono text-[10px] uppercase tracking-wider bg-white/10 hover:bg-white/20 disabled:opacity-40"
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        onClick={() => onSave?.(strokes)}
                        data-testid="annotator-save"
                        className="p-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider bg-[#1FA5CF] hover:bg-[#1787AB]"
                    >
                        <Check size={14} weight="bold" /> Save
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * StaticAnnotationsOverlay — read-only renderer for showing saved
 * annotations on top of a photo (used in PDF generation prep canvas
 * and on the Shared portal). Renders nothing if no strokes.
 *
 * The className prop is applied to the outer wrapper so the consumer
 * has full control over sizing (e.g. `w-16 h-16` for a thumbnail or
 * `w-full max-h-64` for a preview). The internal <img> always fills
 * its wrapper via absolute positioning so the canvas overlay aligns
 * to the same bounding box.
 */
export function AnnotationOverlay({ photoSrc, annotations = [], className = '' }) {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const imgRef = useRef(null);

    useEffect(() => {
        const fit = () => {
            const img = imgRef.current;
            if (!img) return;
            const r = img.getBoundingClientRect();
            const c = canvasRef.current;
            if (!c) return;
            if (r.width === 0 || r.height === 0) return;
            const dpr = window.devicePixelRatio || 1;
            c.width = r.width * dpr;
            c.height = r.height * dpr;
            c.style.width = `${r.width}px`;
            c.style.height = `${r.height}px`;
            const ctx = c.getContext('2d');
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, r.width, r.height);
            annotations.forEach(s => {
                ctx.strokeStyle = s.colour || '#1FA5CF';
                ctx.fillStyle = s.colour || '#1FA5CF';
                ctx.lineWidth = 3;
                ctx.lineJoin = 'round';
                ctx.lineCap = 'round';
                const w = r.width, h = r.height;
                if (!s.points || s.points.length === 0) return;
                if (s.tool !== 'pen' && s.points.length < 2) return;
                if (s.tool === 'pen') {
                    ctx.beginPath();
                    s.points.forEach((p, i) => {
                        if (i === 0) ctx.moveTo(p.x * w, p.y * h);
                        else ctx.lineTo(p.x * w, p.y * h);
                    });
                    ctx.stroke();
                } else if (s.tool === 'circle') {
                    const [a, b] = s.points;
                    const cx = ((a.x + b.x) / 2) * w;
                    const cy = ((a.y + b.y) / 2) * h;
                    const rx = Math.abs(b.x - a.x) * w / 2;
                    const ry = Math.abs(b.y - a.y) * h / 2;
                    ctx.beginPath();
                    ctx.ellipse(cx, cy, Math.max(rx, 4), Math.max(ry, 4), 0, 0, Math.PI * 2);
                    ctx.stroke();
                } else if (s.tool === 'arrow') {
                    const [a, b] = s.points;
                    const ax = a.x * w, ay = a.y * h, bx = b.x * w, by = b.y * h;
                    ctx.beginPath();
                    ctx.moveTo(ax, ay);
                    ctx.lineTo(bx, by);
                    ctx.stroke();
                    const angle = Math.atan2(by - ay, bx - ax);
                    const head = 10;
                    ctx.beginPath();
                    ctx.moveTo(bx, by);
                    ctx.lineTo(bx - head * Math.cos(angle - Math.PI / 6), by - head * Math.sin(angle - Math.PI / 6));
                    ctx.lineTo(bx - head * Math.cos(angle + Math.PI / 6), by - head * Math.sin(angle + Math.PI / 6));
                    ctx.closePath();
                    ctx.fill();
                }
            });
        };
        fit();
        // Re-fit on image load + window resize + parent layout changes.
        const img = imgRef.current;
        if (img && !img.complete) img.addEventListener('load', fit);
        window.addEventListener('resize', fit);
        let ro;
        if (img && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(fit);
            ro.observe(img);
        }
        return () => {
            if (img) img.removeEventListener('load', fit);
            window.removeEventListener('resize', fit);
            if (ro) ro.disconnect();
        };
    }, [photoSrc, annotations]);

    // No annotations? Just render the photo with the consumer's class.
    if (!annotations || annotations.length === 0) {
        return <img src={photoSrc} alt="" className={className} />;
    }
    // With annotations: consumer's className goes on the <img> (sizes the
    // image), the wrapper is inline-block + relative so it hugs the image
    // and the absolutely-positioned canvas aligns perfectly.
    return (
        <div ref={wrapRef} className="relative inline-block max-w-full" data-testid="annotation-overlay">
            <img
                ref={imgRef}
                src={photoSrc}
                alt=""
                className={className}
                crossOrigin="anonymous"
                draggable={false}
            />
            <canvas
                ref={canvasRef}
                data-testid="annotation-overlay-canvas"
                className="absolute inset-0 pointer-events-none"
            />
        </div>
    );
}

/**
 * Flatten annotations into a single dataURL (PNG) so the PDF can embed
 * the marked-up image without a second drawing pass. Used by Report.jsx
 * during PDF build.
 */
export async function flattenAnnotatedPhoto(photoSrc, annotations) {
    if (!annotations || annotations.length === 0) return photoSrc;
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            const ctx = c.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const w = img.naturalWidth, h = img.naturalHeight;
            // Stroke widths scale with the image so they render usefully
            // at print size — too thin and they vanish on a 4MP photo.
            const strokeWidth = Math.max(4, Math.round(w / 200));
            const headSize = strokeWidth * 4;
            annotations.forEach(s => {
                ctx.strokeStyle = s.colour || '#1FA5CF';
                ctx.fillStyle = s.colour || '#1FA5CF';
                ctx.lineWidth = strokeWidth;
                ctx.lineJoin = 'round';
                ctx.lineCap = 'round';
                if (s.tool === 'pen') {
                    ctx.beginPath();
                    s.points.forEach((p, i) => {
                        if (i === 0) ctx.moveTo(p.x * w, p.y * h);
                        else ctx.lineTo(p.x * w, p.y * h);
                    });
                    ctx.stroke();
                } else if (s.tool === 'circle') {
                    const [a, b] = s.points;
                    const cx = ((a.x + b.x) / 2) * w;
                    const cy = ((a.y + b.y) / 2) * h;
                    const rx = Math.abs(b.x - a.x) * w / 2;
                    const ry = Math.abs(b.y - a.y) * h / 2;
                    ctx.beginPath();
                    ctx.ellipse(cx, cy, Math.max(rx, strokeWidth * 2), Math.max(ry, strokeWidth * 2), 0, 0, Math.PI * 2);
                    ctx.stroke();
                } else if (s.tool === 'arrow') {
                    const [a, b] = s.points;
                    const ax = a.x * w, ay = a.y * h, bx = b.x * w, by = b.y * h;
                    ctx.beginPath();
                    ctx.moveTo(ax, ay);
                    ctx.lineTo(bx, by);
                    ctx.stroke();
                    const angle = Math.atan2(by - ay, bx - ax);
                    ctx.beginPath();
                    ctx.moveTo(bx, by);
                    ctx.lineTo(bx - headSize * Math.cos(angle - Math.PI / 6), by - headSize * Math.sin(angle - Math.PI / 6));
                    ctx.lineTo(bx - headSize * Math.cos(angle + Math.PI / 6), by - headSize * Math.sin(angle + Math.PI / 6));
                    ctx.closePath();
                    ctx.fill();
                }
            });
            resolve(c.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = reject;
        img.src = photoSrc;
    });
}
